interface GuideImageProps {
  src: string;
  alt: string;
  caption: string;
  eager?: boolean;
}

function GuideImage({ src, alt, caption, eager = false }: GuideImageProps) {
  return (
    <figure className="activate-figure">
      <img src={src} alt={alt} loading={eager ? 'eager' : 'lazy'} />
      <figcaption>{caption}</figcaption>
    </figure>
  );
}

function OfficialLink({ href, children }: { href: string; children: string }) {
  return (
    <a className="activate-action" href={href} target="_blank" rel="noreferrer">
      {children}<span aria-hidden="true">↗</span>
    </a>
  );
}

export function ActivatePage() {
  return (
    <article className="page activate-page">
      <header className="activate-hero">
        <span className="eyebrow">AI Pro 18个月 · 激活帮助</span>
        <h1>激活教程与常见问题</h1>
        <p>先照教程操作；遇到报错，点下面对应提示。</p>
      </header>

      <nav className="activate-nav" aria-label="本页目录">
        <a href="#normal">正常激活</a>
        <a href="#eligibility">地区不支持</a>
        <a href="#link-needs-new">需要新链接</a>
        <a href="#subscription-in-use">订阅使用中</a>
        <a href="#offer-redeemed">优惠已兑换</a>
        <a href="#generic-error">出了点问题</a>
        <a href="#refund">退款与质保</a>
      </nav>

      <section className="activate-section" id="normal" aria-labelledby="normal-title">
        <div className="activate-section-heading">
          <span>01</span>
          <div>
            <p className="activate-kicker">正常流程</p>
            <h2 id="normal-title">五步完成激活</h2>
          </div>
        </div>

        <div className="activate-prerequisites single-prerequisite">
          <div className="activate-alert is-advisory">
            <strong>Google 账号注册未满 7 天？先了解再激活</strong>
            <p>开通会员一般不会因会员本身导致封号；但刚注册的 Google 账号风控通常更高，无法保证账号不受限制。这里按 Google 账号创建时间算，不是开始使用 Gemini 的时间。</p>
            <p><strong>理解并能接受这类风险，再按下方步骤激活。</strong>如果不想承担，请勿打开激活链接，可申请退款，原因选择「与卖家协商一致」。</p>
          </div>
        </div>

        <ol className="activation-steps">
          <li>
            <div className="activation-step-copy">
              <span>步骤 1</span>
              <h3>先打开Gemini，登录自己的账号</h3>
              <p><strong>用Chrome或Safari打开下面的地址，登录准备开通会员的Google账号。</strong></p>
              <div className="activate-actions-inline">
                <OfficialLink href="https://gemini.google.com/">打开Gemini并登录</OfficialLink>
              </div>
              <p className="instruction-note">https://gemini.google.com/</p>
            </div>
            <GuideImage src="/activate/google-login.png" alt="Google账号登录页面，箭头指向账号输入框" caption="登录完成后，在同一个浏览器里继续下一步。" eager />
          </li>
          <li>
            <div className="activation-step-copy">
              <span>步骤 2</span>
              <h3>复制链接，粘贴到浏览器打开</h3>
              <p><strong>长按聊天里的激活链接，完整复制。</strong>回到刚才登录Gemini的浏览器，新建标签页，粘贴到顶部地址栏，再点「前往」或按回车。</p>
              <p className="instruction-note">不要复制「激活链接」标题，也不要漏掉链接末尾。</p>
            </div>
            <div className="copy-link-pictures">
              <GuideImage src="/activate/copy-link-example.png" alt="聊天中的激活链接，红框表示需要完整复制的范围" caption="① 完整复制聊天里的链接；图中链接已失效，仅作示例。" eager />
              <GuideImage src="/activate/browser-address-example.png" alt="将复制的激活链接粘贴到浏览器顶部地址栏" caption="② 粘贴到浏览器顶部地址栏，再点前往" />
            </div>
          </li>
          <li>
            <div className="activation-step-copy">
              <span>步骤 3</span>
              <h3>确认右上角是正确账号</h3>
              <p><strong>点右上角头像，核对账号。</strong>不是要开通的账号，请先切换；激活后无法主动取消。</p>
            </div>
            <GuideImage
              src="/activate/activation-account-new.png"
              alt="已脱敏的Google账号确认区域"
              caption="右上角账号信息已脱敏；请在自己的页面确认账号。"
            />
          </li>
          <li>
            <div className="activation-step-copy">
              <span>步骤 4</span>
              <h3>先看页面显示的到期时间</h3>
              <p><strong>页面下方会写明预计到期日期。</strong>这是激活前的预览，还需要完成下一步。</p>
            </div>
            <GuideImage
              src="/activate/activation-expiry.png"
              alt="Google One激活页面显示预计到期日期"
              caption="日期以你自己的页面为准；实际权益存在提前结束的可能。"
            />
          </li>
          <li>
            <div className="activation-step-copy">
              <span>步骤 5</span>
              <h3>点击右下角「激活方案」</h3>
              <p>账号和预计到期时间都确认无误后，再点击一次完成激活。</p>
            </div>
            <GuideImage
              src="/activate/activation-button-new.png"
              alt="Google One激活页面右下角的激活方案按钮"
              caption="只需点击一次，提交后等待页面显示结果。"
            />
          </li>
        </ol>

        <div className="activation-success-check">
          <div>
            <p className="activate-kicker">激活成功页面</p>
            <h3>看到“您是 Google AI Pro 会员”就完成了</h3>
            <p>点击「激活方案」后出现下图，表示激活成功。点击页面下方按钮即可前往Gemini使用，无需继续修改地区或付款资料。</p>
          </div>
          <GuideImage
            src="/activate/activation-success.png"
            alt="Google One激活成功页面，显示您是Google AI Pro会员"
            caption="这是激活完成后的成功提示。"
          />
        </div>

        <div className="activation-success-check">
          <div>
            <p className="activate-kicker">激活后确认</p>
            <h3>Gemini头像旁显示“Pro”就是成功</h3>
            <p>打开Gemini并点击左下角头像；账号旁出现“Pro”，说明会员已经生效，不需要继续操作地区、家庭组或付款资料。</p>
          </div>
          <GuideImage
            src="/activate/gemini-pro-confirmation.png"
            alt="Gemini页面账号旁显示Pro标识"
            caption="图中的账号信息已经遮挡，重点看头像旁的“Pro”。"
          />
        </div>
      </section>

      <section className="activate-section" id="eligibility" aria-labelledby="eligibility-title">
        <div className="activate-section-heading">
          <span>02</span>
          <div>
            <p className="activate-kicker">只有无法激活时才处理</p>
            <h2 id="eligibility-title">地区不支持？先换节点再刷新</h2>
          </div>
        </div>

        <div className="activate-subsection">
          <div className="region-first-action">
            <span className="activate-kicker">看到下方两种提示，先做这一步</span>
            <h3>开全局模式 → 换节点 → 刷新原页面</h3>
            <p><strong>不建议使用香港节点。</strong>每换一个节点，就刷新一次；恢复正常后即可返回激活。</p>
            <small>账号地区显示中国也可以先正常激活；只有遇到上述报错才换节点。仍不行，再看下方账号检查。</small>
          </div>
          <div className="activate-image-pair">
            <GuideImage
              src="/activate/google-one-region-unavailable.png"
              alt="Google One提示无法使用，存储空间方案不面向所在地点提供"
              caption="Google One页面的地区不可用提示。"
            />
            <GuideImage
              src="/activate/gemini-region-unavailable.png"
              alt="Gemini提示目前不支持所在地区"
              caption="Gemini页面的地区不可用提示。"
            />
          </div>
          <h3 className="followup-check-title">换过可用节点仍不行，再检查这些</h3>
          <ol className="official-step-list" aria-label="地区排查顺序">
            <li>
              <div><strong>测试 Gemini</strong><span>确认同一账号能否正常打开并发送消息。</span></div>
              <OfficialLink href="https://gemini.google.com/">① 测试Gemini</OfficialLink>
            </li>
            <li>
              <div><strong>看「国家/地区版本」</strong><span>这里只确认账号情况，不要因为显示中国就直接申请修改。</span></div>
              <OfficialLink href="https://policies.google.com/terms">② 查看账号地区</OfficialLink>
            </li>
            <li>
              <div><strong>仍提示账号资格不符时</strong><span>中国地区可能影响资格，可考虑官方地区申请；大多数情况约2小时，实际以Google审核为准。</span></div>
              <OfficialLink href="https://policies.google.com/country-association-form?source=policies-site">③ 按需申请地区修改</OfficialLink>
            </li>
          </ol>
        </div>

        <div className="activate-subsection" id="payment-profile">
          <h3>存在付款资料时，关闭后重新尝试</h3>
          <p><strong>设置页最底部 → 支付资料状态 → 关闭支付资料。</strong>没有付款资料就跳过。</p>
          <p className="instruction-note">关闭前确认没有仍在使用的订阅、余额或付款服务；关闭可能影响这些服务。</p>
          <div className="activate-actions-inline">
            <OfficialLink href="https://payments.google.com/gp/w/home/settings">打开Google付款设置</OfficialLink>
          </div>
          <ol className="plain-step-list">
            <li>确认当前登录的是准备激活的账号。</li>
            <li>拉到设置页最底部，找到“支付资料状态”。</li>
            <li>点击“关闭支付资料”，按页面提示确认。</li>
            <li>重新登录账号，再打开原激活链接。</li>
          </ol>
          <GuideImage
            src="/illustrations/payment-profile-close-example.png"
            alt="Google付款中心设置页面的关闭支付资料位置"
            caption="关闭付款资料会影响现有付款服务和余额；请先确认这份资料不再使用。"
          />
        </div>

        <div className="activate-subsection" id="family-group">
          <h3>检查并退出家庭组</h3>
          <p><strong>在家庭组中：按官方页面提示退出，再重试原链接。</strong>不在家庭组就跳过。退出会失去家庭共享权益，并可能受重新加入限制；管理员不要直接删除整个家庭组。</p>
          <div className="activate-actions-inline">
            <OfficialLink href="https://myaccount.google.com/family/details">检查Google家庭组</OfficialLink>
          </div>
        </div>

        <div className="activate-subsection" id="final-region-retry">
          <h3>其他检查完成后，再换一个可用节点</h3>
          <p><strong>换一个刚才没试过的节点，重新打开原激活链接。</strong>不要用香港节点；激活成功后看 Gemini 左下角是否有 Pro 标识，就不用继续排查。</p>
          <p className="instruction-note">手头可用节点都试过、账号问题也排查完，仍无法激活且链接未被使用时，可选择「与卖家协商一致」申请退款，由卖家核实处理。不要无限重复更换节点或申请修改地区。</p>
        </div>

        <div className="activate-subsection" id="generic-error">
          <h3>页面显示“出了点问题”</h3>
          <p><strong>试试：创建一个Gem → 发「Hi」→ 刷新主页。</strong>此方法不保证所有账号都恢复；失败后发截图给在线客服。</p>
          <GuideImage
            src="/activate/gemini-generic-error.png"
            alt="Gemini页面显示出了点问题，请稍后再试"
            caption="请按页面原文判断，不要把通用报错误当成地区问题。"
          />
          <div className="activate-actions-inline">
            <OfficialLink href="https://gemini.google.com/gems/create">打开Gem创建页</OfficialLink>
          </div>
          <ol className="plain-step-list">
            <li>随便填写一个测试名称并保存。</li>
            <li>在预览窗口发送“Hi”。</li>
            <li>收到正常回复后刷新Gemini主页面。</li>
            <li>主页面恢复后，重新打开原激活链接。</li>
          </ol>
        </div>

      </section>

      <section className="activate-section" id="link-status" aria-labelledby="link-status-title">
        <div className="activate-section-heading">
          <span>03</span>
          <div>
            <p className="activate-kicker">按页面原文判断</p>
            <h2 id="link-status-title">链接提示是什么意思？</h2>
          </div>
        </div>

        <div className="status-guide-grid">
          <article className="status-guide-card" id="link-needs-new">
            <GuideImage
              src="/activate/link-needs-new.png"
              alt="Google One提示您需要新的激活链接"
              caption="首次打开就看到这个提示，当前链接不能继续激活。"
            />
            <h3>“您需要新的激活链接”</h3>
            <p><strong>先回闲鱼找在线客服。</strong>把这张报错截图发来，先核实是否可以补发新链接。</p>
          </article>

          <article className="status-guide-card" id="subscription-in-use">
            <GuideImage
              src="/activate/subscription-in-use.png"
              alt="Google One提示订阅已在使用中"
              caption="成功激活后重新打开链接，也可能出现这个提示。"
            />
            <h3>“订阅已在使用中”</h3>
            <p><strong>先查会员有没有到账。</strong>有权益就不用处理；没有权益，回闲鱼发截图给在线客服核实补发。</p>
            <OfficialLink href="https://one.google.com/">检查Google One会员</OfficialLink>
          </article>

          <article className="status-guide-card is-wide" id="offer-redeemed">
            <GuideImage
              src="/activate/offer-redeemed.png"
              alt="Google One提示优惠已兑换，账号兑换过此优惠或类似优惠"
              caption="看到这句表示当前账号参加过此优惠或类似优惠。"
            />
            <h3>“优惠已兑换”</h3>
            <p><strong>换一个没参加过同类优惠的账号。</strong>当前账号不能再次参加；没有合适账号可申请退款。</p>
          </article>
        </div>
      </section>

      <section className="activate-section refund-warranty-section" id="refund" aria-labelledby="refund-title">
        <div className="activate-section-heading">
          <span>04</span>
          <div>
            <p className="activate-kicker">未激活时处理</p>
            <h2 id="refund-title">退款与质保说明</h2>
          </div>
        </div>

        <div className="refund-reason-card">
          <span>退款原因请选择</span>
          <strong>与卖家协商一致</strong>
          <p>仅适用于链接尚未激活或链接本身存在问题。激活成功后没有主动取消入口，也不能恢复为未激活状态。</p>
        </div>

        <div className="warranty-grid">
          <section>
            <h3>不承诺稳定使用18个月</h3>
            <p>兑换页面会显示 AI Pro 18个月，但会员权益可能提前失效或掉订阅。本商品默认仅保障首次成功兑换。</p>
          </section>
          <section>
            <h3>14天有限质保</h3>
            <p>确认收货并给予好评，可赠送发货之日起14天有限质保。超过14天不提供售后；账号封禁、违规使用等账号自身原因不在保障范围。</p>
          </section>
          <section>
            <h3>售后截图要求</h3>
            <p>14天内权益异常，请在闲鱼聊天发送Google One会员方案页或5TB存储页的完整截图，不要发送账号、密码或Cookie。</p>
          </section>
          <section>
            <h3>商品范围</h3>
            <p>仅提供 AI Pro 会员兑换，不包含 API Key、API额度，也不保证反重力等第三方工具可以使用。</p>
          </section>
        </div>

        <div className="activate-final-actions">
          <a className="primary-button" href="#normal">返回正常激活步骤</a>
        </div>
      </section>
    </article>
  );
}
